import { useState } from 'react';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import { CadencePanel } from '../components/CadencePanel';
import { FollowUpQueuePanel } from '../components/FollowUpQueuePanel';
import { TemplatesPanel } from '../components/TemplatesPanel';

const TABS = [
  { key: 'queue', label: 'Follow-up queue' },
  { key: 'templates', label: 'Templates' },
  { key: 'cadence', label: 'Cadence' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

/**
 * Outreach & messaging kit (PE-2188, backend PE-2187) — replaces the static tcs-outreach-kit.
 *
 * The page holds what spans contacts (who is due, the template library, the cadence); the
 * per-contact half — brand card, AI cold-message draft, send log — lives on the contact's own
 * page, where the conversation happens.
 *
 * TCS-staff only: gated on SALES_OUTREACH_VIEW, writes on SALES_OUTREACH_MANAGE.
 */
export function OutreachPage() {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.SALES_OUTREACH_MANAGE);
  const [tab, setTab] = useState<TabKey>('queue');

  return (
    <div className="py-6 max-w-7xl mx-auto px-2">
      <div className="mb-6">
        <h1 className="text-[23px] font-bold text-foreground">Outreach</h1>
        <p className="text-[14px] text-muted-foreground mt-1">
          Who to follow up with, the message templates staff start from, and how often each stage
          comes due.
        </p>
      </div>

      <div className="border-b border-border mb-6">
        <nav className="flex gap-6 overflow-x-auto" aria-label="Outreach sections">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              aria-current={tab === t.key ? 'page' : undefined}
              className={`pb-3 min-h-10 text-sm transition-colors border-b-2 -mb-px ${
                tab === t.key
                  ? 'border-foreground text-foreground font-medium'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'queue' && <FollowUpQueuePanel />}
      {tab === 'templates' && <TemplatesPanel canManage={canManage} />}
      {tab === 'cadence' && <CadencePanel canManage={canManage} />}
    </div>
  );
}
